import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";

export default function CheckoutSuccess() {
  const { t } = useI18n();
  const location = useLocation();
  const { clear } = useCart();
  const [status, setStatus] = useState({ payment_status: "checking", tries: 0 });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) { setStatus({ payment_status: "unknown" }); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus({ payment_status: "paid", data });
          clear();
          return;
        }
        if (data.status === "expired") { setStatus({ payment_status: "expired" }); return; }
      } catch { /* noop */ }
      attempts += 1;
      if (attempts >= 8) { setStatus({ payment_status: "timeout" }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search, clear]);

  return (
    <div data-testid="checkout-success-page" className="pt-40 pb-32 bg-[#F7F7F6] min-h-screen">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        {status.payment_status === "checking" && (
          <>
            <p data-testid="checking-status" className="font-mono-label text-[11px] text-neutral-500 mb-8">[ VERIFYING ]</p>
            <h1 className="font-serif-display font-light text-5xl md:text-6xl mb-6">{t("checking")}</h1>
            <div className="mt-10 mx-auto w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
          </>
        )}
        {status.payment_status === "paid" && (
          <>
            <p className="font-mono-label text-[11px] text-neutral-500 mb-8">[ CONFIRMED ]</p>
            <h1 data-testid="success-title" className="font-serif-display font-light text-5xl md:text-7xl mb-8">{t("success_title")}</h1>
            <p className="text-neutral-600 max-w-lg mx-auto">{t("success_sub")}</p>
            {status.data?.order && (
              <div className="mt-12 border border-neutral-300 bg-white text-left p-8">
                <p className="font-mono-label text-[10px] text-neutral-500 mb-4">[ ORDER #{status.data.order.id.slice(0, 8).toUpperCase()} ]</p>
                {status.data.order.items?.map((it, i) => (
                  <div key={i} className="flex justify-between border-t border-neutral-200 py-3">
                    <span>{it.name} · {it.dosage} × {it.quantity}</span>
                    <span className="font-serif-display">€{Number(it.line_total).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between mt-3 pt-3 border-t border-black">
                  <span className="font-serif-display text-xl">Total</span>
                  <span className="font-serif-display text-xl">€{Number(status.data.order.total).toFixed(2)}</span>
                </div>
              </div>
            )}
            <div className="mt-10 flex gap-4 justify-center">
              <Link to="/shop" className="btn-sharp">Continue shopping</Link>
              <Link to="/account" className="btn-solid">View orders →</Link>
            </div>
          </>
        )}
        {["timeout", "expired", "unknown"].includes(status.payment_status) && (
          <>
            <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-6">Payment status unclear</h1>
            <p className="text-neutral-600">Please contact support if the amount was charged.</p>
            <Link to="/shop" className="btn-sharp mt-8 inline-flex">Back to shop</Link>
          </>
        )}
      </div>
    </div>
  );
}
